<template>
  <div class="widget-renderer">
    <!-- Legacy content support -->
    <div 
      v-if="hasLegacyContent && !hasWidgets" 
      class="legacy-content"
      v-html="processedLegacyContent"
    ></div>
    
    <!-- Widget-based content -->
    <div 
      v-else-if="hasWidgets" 
      class="widgets-content"
    >
      <template v-for="(widget, widgetIndex) in sortedWidgets" :key="widget.id">
        <div
          :style="getWidgetWrapperStyle(widget)"
          :class="getWidgetWrapperClass(widget)"
        >
          <TextWidget v-if="widget.type === 'text'" :widget="widget" />
          <EnhancedTextWidget v-else-if="widget.type === 'enhanced-text'" :widget="widget" />
          <ButtonWidget v-else-if="widget.type === 'button'" :widget="widget" />
          <ImageWidget v-else-if="widget.type === 'image'" :widget="widget" :priority="isFirstSection && widgetIndex === 0" />
          <SpacerWidget v-else-if="widget.type === 'spacer'" :widget="widget" />
          <DividerWidget v-else-if="widget.type === 'divider'" :widget="widget" />
          <VideoWidget v-else-if="widget.type === 'video'" :widget="widget" />
          <GalleryWidget v-else-if="widget.type === 'gallery'" :widget="widget" />
          <HtmlWidget v-else-if="widget.type === 'html'" :widget="widget" />
          <PostCardsWidget v-else-if="widget.type === 'post-cards'" :widget="widget" />
          <ProductCardsWidget v-else-if="widget.type === 'product-cards'" :widget="widget" />
          <TourCardsWidget v-else-if="widget.type === 'tour-cards'" :widget="widget" />
          <TwoColumnsWidget v-else-if="widget.type === 'two-columns'" :widget="widget" />
          <ThreeColumnsWidget v-else-if="widget.type === 'three-columns'" :widget="widget" />
          <FourColumnsWidget v-else-if="widget.type === 'four-columns'" :widget="widget" />
          <ContactFormWidget v-else-if="widget.type === 'contact-form'" :widget="widget" />
          <AccordionWidget v-else-if="widget.type === 'accordion'" :widget="widget" />
          <TabsWidget v-else-if="widget.type === 'tabs'" :widget="widget" />
          <ProgressBarWidget v-else-if="widget.type === 'progress-bar'" :widget="widget" />
          <CounterWidget v-else-if="widget.type === 'counter'" :widget="widget" />
          <SocialMediaWidget v-else-if="widget.type === 'social-media'" :widget="widget" />
          <FacebookPageWidget v-else-if="widget.type === 'facebook-page'" :widget="widget" />
          <ReviewButtonWidget v-else-if="widget.type === 'review-button'" :widget="widget" />
          <ReviewsDisplayWidget v-else-if="widget.type === 'reviews-display'" :widget="widget" />
          <div v-else class="unknown-widget-type text-muted text-center p-3">
            <i class="bi bi-question-circle"></i>
            <small>Unknown widget type: {{ (widget as any).type }}</small>
          </div>
        </div>
      </template>
    </div>
    
    <!-- Empty state -->
    <div 
      v-else-if="!hasLegacyContent && !hasWidgets" 
      class="empty-content text-muted text-center p-3"
    >
      <small>Δεν υπάρχει περιεχόμενο για αυτή την ενότητα</small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Widget, BaseWidget } from '@/types/pagebuilder'
import { processTextContent } from '@/utils/textProcessing'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'
import { widgetHandlesOwnFrame } from '@/utils/widgetFramePolicy'

// Import widget components
import TextWidget from '@/components/admin/pagebuilder/TextWidget.vue'
import EnhancedTextWidget from '@/components/admin/pagebuilder/EnhancedTextWidget.vue'
import ButtonWidget from '@/components/admin/pagebuilder/ButtonWidget.vue'
import ImageWidget from '@/components/admin/pagebuilder/ImageWidget.vue'
import SpacerWidget from '@/components/admin/pagebuilder/SpacerWidget.vue'
import DividerWidget from '@/components/admin/pagebuilder/DividerWidget.vue'

// Import all widget components
import VideoWidget from './VideoWidget.vue'
import GalleryWidget from './GalleryWidget.vue'
import HtmlWidget from './HtmlWidget.vue'
import PostCardsWidget from './PostCardsWidget.vue'
import ProductCardsWidget from './ProductCardsWidget.vue'
import TourCardsWidget from './TourCardsWidget.vue'
import TwoColumnsWidget from './TwoColumnsWidget.vue'
import ThreeColumnsWidget from './ThreeColumnsWidget.vue'
import FourColumnsWidget from './FourColumnsWidget.vue'
import ContactFormWidget from './ContactFormWidget.vue'
import AccordionWidget from './AccordionWidget.vue'
import TabsWidget from './TabsWidget.vue'
import ProgressBarWidget from './ProgressBarWidget.vue'
import CounterWidget from './CounterWidget.vue'
import SocialMediaWidget from './SocialMediaWidget.vue'
import FacebookPageWidget from './FacebookPageWidget.vue'
import ReviewButtonWidget from '@/components/widgets/ReviewButtonWidget.vue'
import ReviewsDisplayWidget from '@/components/widgets/ReviewsDisplayWidget.vue'

interface Props {
  section: {
    content?: string
    widgets?: Widget[]
    [key: string]: any
  }
  isFirstSection?: boolean
}

const props = defineProps<Props>()

const hasLegacyContent = computed(() => {
  return props.section.content && props.section.content.trim() !== ''
})

const hasWidgets = computed(() => {
  return props.section.widgets && Array.isArray(props.section.widgets) && props.section.widgets.length > 0
})

const sortedWidgets = computed(() => {
  if (!props.section.widgets) return []
  
  // Normalize widgets - ensure all required properties have default values
  const normalized = [...props.section.widgets].map(widget => {
    if (widget.type === 'gallery') {
      return {
        ...widget,
        styleMode: widget.styleMode || 'simple',
        showTitles: widget.showTitles !== undefined ? widget.showTitles : false,
        imageHeight: widget.imageHeight || '200px',
        imageBorderRadius: widget.imageBorderRadius || '8px',
        columns: widget.columns || 3,
        spacing: widget.spacing || '1rem',
        lightbox: widget.lightbox !== undefined ? widget.lightbox : true,
        mobileLayout: widget.mobileLayout || 'grid',
        carouselSpeed: widget.carouselSpeed ?? 20,
        carouselFadeEdges: widget.carouselFadeEdges ?? false,
        carouselFadeSize: widget.carouselFadeSize || '10%',
      }
    }
    if (widget.type === 'enhanced-text') {
      const et = widget as import('@/types/pagebuilder').EnhancedTextWidget
      const normalized = { ...widget } as typeof et
      if (et.backgroundType === 'image' && !et.backgroundSize) {
        normalized.backgroundSize = 'cover'
      }
      if (
        et.backgroundType === 'image' &&
        et.imageMode !== 'card' &&
        !et.backgroundImageHeight &&
        !et.minHeight &&
        !et.height
      ) {
        normalized.backgroundImageHeight = '280px'
      }
      return normalized
    }
    return widget
  })
  
  // Sort widgets by their order property
  return normalized.sort((a, b) => a.order - b.order)
})

const processedLegacyContent = computed(() => {
  return processTextContent(props.section.content || '')
})

function getWidgetWrapperStyle(widget: BaseWidget): Record<string, string> {
  if (widgetHandlesOwnFrame(widget.type)) {
    return {}
  }

  const styles: Record<string, string> = {}

  // Dimensions
  if (widget.width) styles.width = widget.width
  if (widget.maxWidth) styles.maxWidth = widget.maxWidth
  if (widget.height) styles.height = widget.height
  if (widget.minHeight) styles.minHeight = widget.minHeight

  // Spacing – shorthand takes precedence over individual sides
  if (widget.padding) {
    styles.padding = widget.padding
  } else {
    if (widget.paddingTop) styles.paddingTop = widget.paddingTop
    if (widget.paddingRight) styles.paddingRight = widget.paddingRight
    if (widget.paddingBottom) styles.paddingBottom = widget.paddingBottom
    if (widget.paddingLeft) styles.paddingLeft = widget.paddingLeft
  }

  if (widget.margin) {
    styles.margin = widget.margin
  } else {
    if (widget.marginTop) styles.marginTop = widget.marginTop
    if (widget.marginRight) styles.marginRight = widget.marginRight
    if (widget.marginBottom) styles.marginBottom = widget.marginBottom
    if (widget.marginLeft) styles.marginLeft = widget.marginLeft
  }

  // Background (solid or gradient)
  Object.assign(styles, resolveWidgetBackgroundStyle(widget))
  if (widget.backgroundImage) styles.backgroundImage = `url(${widget.backgroundImage})`
  if (widget.backgroundSize) styles.backgroundSize = widget.backgroundSize
  if (widget.backgroundPosition) styles.backgroundPosition = widget.backgroundPosition
  if (widget.backgroundRepeat) styles.backgroundRepeat = widget.backgroundRepeat
  if (widget.backgroundAttachment) styles.backgroundAttachment = widget.backgroundAttachment

  // Border
  if (widget.borderStyle && widget.borderStyle !== 'none') {
    styles.borderStyle = widget.borderStyle
    if (widget.borderWidth) styles.borderWidth = widget.borderWidth
    if (widget.borderColor) styles.borderColor = widget.borderColor
  }

  // Border radius – shorthand or per-corner
  if (widget.borderRadius) {
    styles.borderRadius = widget.borderRadius
  } else {
    if (widget.borderTopLeftRadius) styles.borderTopLeftRadius = widget.borderTopLeftRadius
    if (widget.borderTopRightRadius) styles.borderTopRightRadius = widget.borderTopRightRadius
    if (widget.borderBottomRightRadius) styles.borderBottomRightRadius = widget.borderBottomRightRadius
    if (widget.borderBottomLeftRadius) styles.borderBottomLeftRadius = widget.borderBottomLeftRadius
  }

  // Box shadow – full string or built from parts
  if (widget.boxShadow) {
    styles.boxShadow = widget.boxShadow
  } else if (widget.boxShadowHorizontal || widget.boxShadowVertical) {
    const h = widget.boxShadowHorizontal || '0px'
    const v = widget.boxShadowVertical || '0px'
    const b = widget.boxShadowBlur || '10px'
    const s = widget.boxShadowSpread || '0px'
    const c = widget.boxShadowColor || 'rgba(0,0,0,0.1)'
    const p = widget.boxShadowPosition === 'inset' ? 'inset ' : ''
    styles.boxShadow = `${p}${h} ${v} ${b} ${s} ${c}`
  }

  // Opacity
  if (widget.opacity) styles.opacity = widget.opacity

  // Transform – full string or built from parts
  const transforms: string[] = []
  if (widget.rotate) transforms.push(`rotate(${widget.rotate})`)
  if (widget.scaleX || widget.scaleY) {
    const sx = widget.scaleX || '1'
    const sy = widget.scaleY || widget.scaleX || '1'
    transforms.push(`scale(${sx}, ${sy})`)
  }
  if (widget.translateX || widget.translateY) {
    const tx = widget.translateX || '0px'
    const ty = widget.translateY || '0px'
    transforms.push(`translate(${tx}, ${ty})`)
  }
  if (widget.skewX) transforms.push(`skewX(${widget.skewX})`)
  if (widget.skewY) transforms.push(`skewY(${widget.skewY})`)
  if (widget.transform) {
    styles.transform = widget.transform
  } else if (transforms.length > 0) {
    styles.transform = transforms.join(' ')
  }
  if (widget.transformOrigin) styles.transformOrigin = widget.transformOrigin

  // CSS filters
  const filters: string[] = []
  if (widget.blur) filters.push(`blur(${widget.blur})`)
  if (widget.brightness) filters.push(`brightness(${widget.brightness})`)
  if (widget.contrast) filters.push(`contrast(${widget.contrast})`)
  if (widget.saturate) filters.push(`saturate(${widget.saturate})`)
  if (widget.hueRotate) filters.push(`hue-rotate(${widget.hueRotate})`)
  if (widget.grayscale) filters.push(`grayscale(${widget.grayscale})`)
  if (filters.length > 0) styles.filter = filters.join(' ')

  // Transition
  if (widget.transitionDuration) {
    const timing = widget.transitionTimingFunction || 'ease'
    styles.transition = `all ${widget.transitionDuration} ${timing}`
  }

  // Position & display
  if (widget.position) styles.position = widget.position
  if (widget.top) styles.top = widget.top
  if (widget.right) styles.right = widget.right
  if (widget.bottom) styles.bottom = widget.bottom
  if (widget.left) styles.left = widget.left
  if (widget.zIndex) styles.zIndex = widget.zIndex
  if (widget.display) styles.display = widget.display

  // Flexbox
  if (widget.flexDirection) styles.flexDirection = widget.flexDirection
  if (widget.justifyContent) styles.justifyContent = widget.justifyContent
  if (widget.alignItems) styles.alignItems = widget.alignItems
  if (widget.gap) styles.gap = widget.gap

  // Entrance animation
  if (widget.entranceAnimation && widget.entranceAnimation !== 'none') {
    const duration = widget.animationDuration || '0.5s'
    const delay = widget.animationDelay || '0s'
    const iterations = widget.animationIterationCount || '1'
    styles.animation = `${widget.entranceAnimation} ${duration} ease-out ${delay} ${iterations} both`
  }

  // Custom CSS (simple key:value pairs only)
  if (widget.customCSS) {
    widget.customCSS.split(';').forEach(rule => {
      const colonIdx = rule.indexOf(':')
      if (colonIdx === -1) return
      const property = rule.slice(0, colonIdx).trim()
      const value = rule.slice(colonIdx + 1).trim()
      if (property && value) {
        const camelProperty = property.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
        styles[camelProperty] = value
      }
    })
  }

  return styles
}

function getWidgetWrapperClass(widget: BaseWidget): string {
  if (widgetHandlesOwnFrame(widget.type)) {
    return ''
  }

  const classes: string[] = []

  // Responsive visibility
  if (widget.hidden_on_mobile) classes.push('d-none d-md-block')
  if (widget.hidden_on_tablet) classes.push('d-md-none d-lg-block')
  if (widget.hidden_on_desktop) classes.push('d-lg-none')

  // Custom class
  if (widget.customClass) classes.push(widget.customClass)

  // Animation marker class
  if (widget.entranceAnimation && widget.entranceAnimation !== 'none') {
    classes.push('widget-animated')
  }

  return classes.join(' ')
}
</script>

<style scoped>
.widget-renderer {
  width: 100%;
}

.legacy-content {
  line-height: 1.6;
}

.widgets-content {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.unknown-widget-type {
  border: 1px dashed #dee2e6;
  border-radius: 4px;
  background: #f8f9fa;
}

.empty-content {
  opacity: 0.7;
  font-style: italic;
}
</style>